/**
 * Business Settings Component
 *
 * Allows users to edit their business profile after initial onboarding.
 * Employer NI Number is readonly since it's the business identifier.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  useBusinessProfile,
  useUpdateBusinessProfile,
} from '@/hooks/business/useBusinessProfile';
import {
  BusinessProfileUpdateRequest,
  FormatType,
  BAHAMAS_ISLANDS,
} from '@/types/business';

interface BusinessFormData {
  company_name: string;
  trading_name: string;
  format_type: FormatType;
  industry_sector: string;
  contact_email: string;
  contact_phone: string;
  island: string;
}

const INDUSTRY_SECTORS = [
  'Hospitality & Tourism',
  'Financial Services',
  'Retail & Wholesale',
  'Construction',
  'Healthcare',
  'Education',
  'Transportation',
  'Manufacturing',
  'Professional Services',
  'Other',
];

export function BusinessSettings() {
  const { data: profile, isLoading } = useBusinessProfile();
  const updateMutation = useUpdateBusinessProfile();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<BusinessFormData>();

  const formatType = watch('format_type');

  // Populate form when profile loads
  useEffect(() => {
    if (profile) {
      reset({
        company_name: profile.company_name || '',
        trading_name: profile.trading_name || '',
        format_type: profile.format_type || 'NON_HOSPITALITY',
        industry_sector: profile.industry_sector || '',
        contact_email: profile.contact_email || '',
        contact_phone: profile.contact_phone || '',
        island: profile.island || '',
      });
    }
  }, [profile, reset]);

  const onSubmit = async (data: BusinessFormData) => {
    try {
      const request: BusinessProfileUpdateRequest = {
        company_name: data.company_name,
        trading_name: data.trading_name || undefined,
        format_type: data.format_type,
        industry_sector: data.industry_sector || undefined,
        contact_email: data.contact_email || undefined,
        contact_phone: data.contact_phone || undefined,
        island: data.island || undefined,
      };

      await updateMutation.mutateAsync(request);
      toast.success('Business profile updated successfully');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update profile';
      toast.error(message);
    }
  };

  const handleCancel = () => {
    if (profile) {
      reset({
        company_name: profile.company_name || '',
        trading_name: profile.trading_name || '',
        format_type: profile.format_type || 'NON_HOSPITALITY',
        industry_sector: profile.industry_sector || '',
        contact_email: profile.contact_email || '',
        contact_phone: profile.contact_phone || '',
        island: profile.island || '',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">
          No business profile found. Please complete onboarding first.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          Business Information
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Update your company details and NIB configuration
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-6 space-y-6">
        {/* Employer NI Number - Readonly */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Employer NI Number
          </label>
          <input
            type="text"
            value={profile.employer_ni_number}
            disabled
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-500">
            Contact support to change your employer NI number
          </p>
        </div>

        {/* Company Name */}
        <div>
          <label
            htmlFor="company_name"
            className="block text-sm font-medium text-gray-700"
          >
            Company Name *
          </label>
          <input
            type="text"
            id="company_name"
            {...register('company_name', {
              required: 'Company name is required',
              minLength: {
                value: 2,
                message: 'Must be at least 2 characters',
              },
            })}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
          {errors.company_name && (
            <p className="mt-1 text-sm text-red-600">
              {errors.company_name.message}
            </p>
          )}
        </div>

        {/* Trading Name */}
        <div>
          <label
            htmlFor="trading_name"
            className="block text-sm font-medium text-gray-700"
          >
            Trading Name
          </label>
          <input
            type="text"
            id="trading_name"
            {...register('trading_name')}
            placeholder="If different from company name"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Business Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Type *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label
              className={`flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                formatType === 'NON_HOSPITALITY'
                  ? 'border-primary-500 bg-primary-50'
                  : ''
              }`}
            >
              <input
                type="radio"
                value="NON_HOSPITALITY"
                {...register('format_type', { required: true })}
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <div className="ml-3">
                <span className="block text-sm font-medium text-gray-900">
                  Non-Hospitality
                </span>
                <span className="block text-xs text-gray-500">
                  Standard C10 format (80-byte)
                </span>
              </div>
            </label>
            <label
              className={`flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                formatType === 'HOSPITALITY'
                  ? 'border-primary-500 bg-primary-50'
                  : ''
              }`}
            >
              <input
                type="radio"
                value="HOSPITALITY"
                {...register('format_type', { required: true })}
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <div className="ml-3">
                <span className="block text-sm font-medium text-gray-900">
                  Hospitality
                </span>
                <span className="block text-xs text-gray-500">
                  Includes gratuity fields (115-byte)
                </span>
              </div>
            </label>
          </div>
        </div>

        {formatType === 'HOSPITALITY' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <svg
                className="h-5 w-5 text-blue-400 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="ml-3 text-sm text-blue-700">
                Hospitality businesses must report employee gratuities/tips
                separately for NIB contribution calculations.
              </p>
            </div>
          </div>
        )}

        {/* Industry Sector */}
        <div>
          <label
            htmlFor="industry_sector"
            className="block text-sm font-medium text-gray-700"
          >
            Industry Sector
          </label>
          <select
            id="industry_sector"
            {...register('industry_sector')}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select an industry...</option>
            {INDUSTRY_SECTORS.map(sector => (
              <option key={sector} value={sector}>
                {sector}
              </option>
            ))}
          </select>
        </div>

        {/* Island */}
        <div>
          <label
            htmlFor="island"
            className="block text-sm font-medium text-gray-700"
          >
            Island
          </label>
          <select
            id="island"
            {...register('island')}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select an island...</option>
            {BAHAMAS_ISLANDS.map(island => (
              <option key={island} value={island}>
                {island}
              </option>
            ))}
          </select>
        </div>

        {/* Contact Email */}
        <div>
          <label
            htmlFor="contact_email"
            className="block text-sm font-medium text-gray-700"
          >
            Business Contact Email
          </label>
          <input
            type="email"
            id="contact_email"
            {...register('contact_email', {
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Invalid email address',
              },
            })}
            placeholder="payroll@company.com"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
          {errors.contact_email && (
            <p className="mt-1 text-sm text-red-600">
              {errors.contact_email.message}
            </p>
          )}
        </div>

        {/* Contact Phone */}
        <div>
          <label
            htmlFor="contact_phone"
            className="block text-sm font-medium text-gray-700"
          >
            Business Contact Phone
          </label>
          <input
            type="tel"
            id="contact_phone"
            {...register('contact_phone')}
            placeholder="(242) 555-1234"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            disabled={!isDirty || updateMutation.isPending}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isDirty || updateMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {updateMutation.isPending ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BusinessSettings;
